import { useState, useRef, useEffect, useCallback } from "react";
import { postIpc } from "../../lib/ipc";
import { apiFetch } from "../../lib/api";
import { useAutopilotStatus } from "../../lib/autopilotStatus";
import { useAuth } from "../../context/AuthContext";

interface TerminalLine {
  type: "prompt" | "output" | "tool" | "tool-result" | "error" | "system" | "quarantine";
  text: string;
  timestamp?: number;
  /** Structured tool data for rich rendering */
  toolId?: string;
  toolName?: string;
  toolInput?: Record<string, unknown>;
  toolOutput?: string;
  toolIsError?: boolean;
  toolStatus?: "executing" | "done" | "error";
  policyDecision?: "allow" | "block" | "quarantine";
  policyReason?: string;
  /** Quarantine approval data */
  approvalId?: string;
  quarantineOutcome?: "pending" | "approved" | "rejected" | "timeout";
}

interface ChatMessage {
  role: "user" | "agent";
  content: string;
  timestamp: string;
  errorCode?: string;
}

interface ProviderInfo {
  id: string;
  display_name: string;
  active: boolean;
}

interface OllamaModel {
  name: string;
  parameter_size?: string;
}

interface ChatWorkbenchProps {
  projectName: string;
}

/* ── Provider icon SVGs (animated) ── */
const PROVIDER_ICONS: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  anthropic: {
    color: "#d4a27f",
    label: "Claude",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-pulse 2s ease-in-out infinite" }}>
        <path d="M13.5 2L6 14h4.5l-1 8L17 10h-4.5l1-8z" fill="currentColor" />
      </svg>
    ),
  },
  claude: {
    color: "#d4a27f",
    label: "Claude CLI",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-pulse 2s ease-in-out infinite" }}>
        <path d="M13.5 2L6 14h4.5l-1 8L17 10h-4.5l1-8z" fill="currentColor" />
      </svg>
    ),
  },
  ollama: {
    color: "#f8f8f2",
    label: "Ollama",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-bounce 1.5s ease infinite" }}>
        <circle cx="12" cy="8" r="5" stroke="currentColor" strokeWidth="2" />
        <path d="M5 20c0-3.87 3.13-7 7-7s7 3.13 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="10" cy="7" r="1" fill="currentColor" />
        <circle cx="14" cy="7" r="1" fill="currentColor" />
      </svg>
    ),
  },
  openai: {
    color: "#74aa9c",
    label: "OpenAI",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-spin 3s linear infinite" }}>
        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 3a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0 14a8 8 0 0 1-6.93-4 8 8 0 0 1 13.86 0A8 8 0 0 1 12 19z" fill="currentColor" />
      </svg>
    ),
  },
  codex: {
    color: "#4f8fff",
    label: "Codex",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-blink 1s step-end infinite" }}>
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M7 9l3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="13" y1="15" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
};

const DEFAULT_PROVIDER_ICON = {
  color: "#bd93f9",
  label: "AI",
  icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" />
      <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" />
      <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
};

/* ── Lightweight markdown renderer ── */
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let inCodeBlock = false;
  let codeLines: string[] = [];
  let codeLang = "";

  const renderInline = (line: string): React.ReactNode => {
    // Bold, italic, inline code, links
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let key = 0;

    while (remaining.length > 0) {
      // Inline code
      const codeMatch = remaining.match(/^`([^`]+)`/);
      if (codeMatch) {
        parts.push(<code key={key++} className="chat-md-inline-code">{codeMatch[1]}</code>);
        remaining = remaining.slice(codeMatch[0].length);
        continue;
      }
      // Bold
      const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
      if (boldMatch) {
        parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }
      // Italic
      const italicMatch = remaining.match(/^\*(.+?)\*/);
      if (italicMatch) {
        parts.push(<em key={key++}>{italicMatch[1]}</em>);
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }
      // Plain text up to next special char
      const nextSpecial = remaining.search(/[`*]/);
      if (nextSpecial === -1) {
        parts.push(remaining);
        break;
      } else if (nextSpecial === 0) {
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
      } else {
        parts.push(remaining.slice(0, nextSpecial));
        remaining = remaining.slice(nextSpecial);
      }
    }
    return parts.length === 1 ? parts[0] : <>{parts}</>;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code block toggle
    if (line.startsWith("```")) {
      if (!inCodeBlock) {
        inCodeBlock = true;
        codeLang = line.slice(3).trim();
        codeLines = [];
      } else {
        elements.push(
          <div key={i} className="chat-md-codeblock">
            {codeLang && <div className="chat-md-codeblock__lang">{codeLang}</div>}
            <pre><code>{codeLines.join("\n")}</code></pre>
          </div>
        );
        inCodeBlock = false;
        codeLang = "";
      }
      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    // Headings
    const h3 = line.match(/^### (.+)/);
    if (h3) { elements.push(<h4 key={i} className="chat-md-h3">{renderInline(h3[1])}</h4>); continue; }
    const h2 = line.match(/^## (.+)/);
    if (h2) { elements.push(<h3 key={i} className="chat-md-h2">{renderInline(h2[1])}</h3>); continue; }
    const h1 = line.match(/^# (.+)/);
    if (h1) { elements.push(<h2 key={i} className="chat-md-h1">{renderInline(h1[1])}</h2>); continue; }

    // Unordered list
    const ul = line.match(/^[-*] (.+)/);
    if (ul) { elements.push(<div key={i} className="chat-md-li">{renderInline(ul[1])}</div>); continue; }

    // Ordered list
    const ol = line.match(/^\d+\. (.+)/);
    if (ol) { elements.push(<div key={i} className="chat-md-li chat-md-li--ordered">{renderInline(ol[1])}</div>); continue; }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) { elements.push(<hr key={i} className="chat-md-hr" />); continue; }

    // Empty line
    if (line.trim() === "") { elements.push(<div key={i} className="chat-md-spacer" />); continue; }

    // Regular paragraph
    elements.push(<p key={i} className="chat-md-p">{renderInline(line)}</p>);
  }

  // Close unclosed code block
  if (inCodeBlock && codeLines.length > 0) {
    elements.push(
      <div key="unclosed-code" className="chat-md-codeblock">
        {codeLang && <div className="chat-md-codeblock__lang">{codeLang}</div>}
        <pre><code>{codeLines.join("\n")}</code></pre>
      </div>
    );
  }

  return <div className="chat-md">{elements}</div>;
}

/* ── Timestamp formatter ── */
function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function nowTimestamp(): string {
  return new Date().toISOString();
}

/* ── Thinking indicator ── */
function ThinkingIndicator() {
  return (
    <div className="chat-thinking-indicator">
      <span className="chat-thinking-indicator__dot" />
      <span className="chat-thinking-indicator__text">AI is thinking...</span>
    </div>
  );
}

/* ── Provider unavailable card ── */
function ProviderUnavailableCard({ message, onConnect }: { message: string; onConnect: () => void }) {
  return (
    <div className="chat-provider-unavailable">
      <div className="chat-provider-unavailable__icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
      <div className="chat-provider-unavailable__body">
        <p className="chat-provider-unavailable__title">No AI provider connected</p>
        <p className="chat-provider-unavailable__desc">
          {message || "Connect Ollama locally or configure an API key in Settings."}
        </p>
      </div>
      <button className="chat-provider-unavailable__btn" onClick={onConnect}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
        Connect Provider
      </button>
    </div>
  );
}

/* ── Provider error card (connected but errored) ── */
function ProviderErrorCard({ message }: { message: string }) {
  return (
    <div className="chat-provider-error">
      <div className="chat-provider-error__icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <span className="chat-provider-error__text">{message}</span>
    </div>
  );
}

/* ── Model selector pill ── */
function ModelPill({
  providers,
  activeProvider,
  models,
  activeModel,
  onSelectProvider,
  onSelectModel,
}: {
  providers: ProviderInfo[];
  activeProvider: string;
  models: OllamaModel[];
  activeModel: string;
  onSelectProvider: (id: string) => void;
  onSelectModel: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pIcon = PROVIDER_ICONS[activeProvider] || DEFAULT_PROVIDER_ICON;

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const displayLabel = activeModel
    ? `${activeProvider} / ${activeModel}`
    : pIcon.label;

  return (
    <div ref={ref} className="chat-model-pill-wrap">
      <button
        onClick={() => setOpen(!open)}
        className="chat-model-pill"
        title="Select provider and model"
      >
        <span className="chat-model-pill__icon" style={{ color: pIcon.color }}>{pIcon.icon}</span>
        <span className="chat-model-pill__label">{displayLabel}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="chat-model-dropdown">
          <div className="chat-model-dropdown__section-title">Providers</div>
          {providers.map((p) => {
            const pi = PROVIDER_ICONS[p.id] || DEFAULT_PROVIDER_ICON;
            return (
              <button
                key={p.id}
                onClick={() => { onSelectProvider(p.id); if (p.id !== "ollama") setOpen(false); }}
                className={`chat-model-dropdown__item ${p.id === activeProvider ? "active" : ""}`}
              >
                <span className="chat-model-dropdown__item-icon" style={{ color: pi.color }}>{pi.icon}</span>
                <span>{p.display_name}</span>
                {p.id === activeProvider && <span className="chat-model-dropdown__active-tag">active</span>}
              </button>
            );
          })}
          {activeProvider === "ollama" && models.length > 0 && (
            <>
              <div className="chat-model-dropdown__section-title" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                Models
              </div>
              {models.map((m) => (
                <button
                  key={m.name}
                  onClick={() => { onSelectModel(m.name); setOpen(false); }}
                  className={`chat-model-dropdown__item ${m.name === activeModel ? "active" : ""}`}
                >
                  {m.parameter_size && <span className="chat-model-dropdown__size">{m.parameter_size}</span>}
                  <span>{m.name}</span>
                  {m.name === activeModel && <span className="chat-model-dropdown__active-tag">selected</span>}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Suggestion cards for empty state ── */
const SUGGESTIONS = [
  { label: "Explain this codebase", icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  )},
  { label: "Write a test for...", icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
    </svg>
  )},
  { label: "Help me debug...", icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
    </svg>
  )},
];

/* ── Sending spinner ── */
function SendingSpinner() {
  return (
    <svg className="chat-send-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

/* ── Policy Eval Card (compact, collapsed by default) ── */
function PolicyEvalCard({ line, resultLine }: { line: TerminalLine; resultLine?: TerminalLine }) {
  const [expanded, setExpanded] = useState(false);
  const isExecuting = line.toolStatus === "executing";
  const isError = line.toolStatus === "error" || line.toolIsError || resultLine?.toolIsError;
  const decision = line.policyDecision || "allow";
  const isBlock = decision === "block";
  const isQuarantine = decision === "quarantine";

  const borderColor = isBlock ? "var(--ot-red, #ff5555)" : isQuarantine ? "var(--ot-yellow, #ffb86c)" : "var(--ot-green, #50fa7b)";
  const badgeLabel = decision.toUpperCase();

  // Compute a compact param summary (e.g., path: "src/main.rs")
  const paramSummary = line.toolInput
    ? Object.entries(line.toolInput).slice(0, 2).map(([k, v]) => {
        const val = typeof v === "string" ? (v.length > 40 ? v.slice(0, 40) + "..." : v) : JSON.stringify(v);
        return `${k}: ${val}`;
      }).join(", ")
    : "";

  // Timing: compute from timestamp difference if result exists
  const timingMs = resultLine?.timestamp && line.timestamp
    ? (resultLine.timestamp - line.timestamp).toFixed(1)
    : null;

  return (
    <div
      className={`policy-eval-card ${expanded ? "policy-eval-card--expanded" : ""}`}
      style={{ borderLeftColor: borderColor }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="policy-eval-card__header">
        <span className="policy-eval-card__name">
          {isExecuting && <span className="policy-eval-card__spinner" />}
          {line.toolName || "tool"}
        </span>
        <span className="policy-eval-card__spacer" />
        {timingMs && <span className="policy-eval-card__timing">{timingMs}ms</span>}
        <span className={`policy-eval-card__badge policy-eval-card__badge--${decision}`}>
          {badgeLabel}
        </span>
      </div>
      {expanded && (
        <div className="policy-eval-card__body">
          {paramSummary && (
            <div className="policy-eval-card__params">{paramSummary}</div>
          )}
          {line.policyReason && (
            <div className="policy-eval-card__reason">Reason: {line.policyReason}</div>
          )}
          {line.toolInput && Object.keys(line.toolInput).length > 0 && (
            <div className="policy-eval-card__section">
              <div className="policy-eval-card__section-label">Input</div>
              <pre className="policy-eval-card__pre">
                {JSON.stringify(line.toolInput, null, 2)}
              </pre>
            </div>
          )}
          {(resultLine?.toolOutput || line.toolOutput) && (
            <div className="policy-eval-card__section">
              <div className="policy-eval-card__section-label">Output</div>
              <pre className={`policy-eval-card__pre ${isError ? "policy-eval-card__pre--error" : ""}`}>
                {resultLine?.toolOutput || line.toolOutput}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Quarantine Approval Card ── */
function QuarantineApprovalCard({ line }: { line: TerminalLine }) {
  const isPending = line.quarantineOutcome === "pending";
  const isApproved = line.quarantineOutcome === "approved";
  const isRejected = line.quarantineOutcome === "rejected" || line.quarantineOutcome === "timeout";

  const borderColor = isPending ? "rgba(255,184,108,0.5)" : isApproved ? "rgba(80,250,123,0.4)" : "rgba(255,85,85,0.4)";
  const bgColor = isPending ? "rgba(255,184,108,0.08)" : isApproved ? "rgba(80,250,123,0.06)" : "rgba(255,85,85,0.06)";

  const handleApprove = async () => {
    if (!line.approvalId) return;
    try {
      await apiFetch(`/api/policy/approve/${line.approvalId}`, { method: "POST" });
    } catch (err) {
      console.warn("[quarantine] approve failed:", err);
    }
  };

  const handleReject = async () => {
    if (!line.approvalId) return;
    try {
      await apiFetch(`/api/policy/reject/${line.approvalId}`, { method: "POST" });
    } catch (err) {
      console.warn("[quarantine] reject failed:", err);
    }
  };

  return (
    <div
      style={{
        margin: "4px 0",
        padding: "8px 10px",
        borderRadius: "6px",
        border: `1.5px solid ${borderColor}`,
        background: bgColor,
        fontFamily: "var(--font-mono, monospace)",
        fontSize: "12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
        {/* Shield icon */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,184,108,0.8)" stroke="none">
          <path d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" />
        </svg>
        <span style={{ color: "#ffb86c", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "10px" }}>
          Quarantine
        </span>
        <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "10px" }}>
          {line.approvalId}
        </span>
      </div>
      <div style={{ color: "rgba(255,255,255,0.85)", marginBottom: "4px" }}>
        <strong style={{ color: "#bd93f9" }}>{line.toolName}</strong>
        <span style={{ color: "rgba(255,255,255,0.5)", marginLeft: "8px" }}>{line.policyReason}</span>
      </div>
      {isPending ? (
        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
          <button
            onClick={handleApprove}
            style={{
              padding: "4px 14px",
              borderRadius: "4px",
              border: "1px solid rgba(80,250,123,0.4)",
              background: "rgba(80,250,123,0.15)",
              color: "#50fa7b",
              fontWeight: 700,
              fontSize: "11px",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Approve
          </button>
          <button
            onClick={handleReject}
            style={{
              padding: "4px 14px",
              borderRadius: "4px",
              border: "1px solid rgba(255,85,85,0.4)",
              background: "rgba(255,85,85,0.15)",
              color: "#ff5555",
              fontWeight: 700,
              fontSize: "11px",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Reject
          </button>
          <span style={{ color: "rgba(255,184,108,0.7)", fontSize: "10px", alignSelf: "center", marginLeft: "auto" }}>
            Awaiting approval (5 min timeout)
          </span>
        </div>
      ) : (
        <div style={{
          marginTop: "4px",
          fontSize: "11px",
          fontWeight: 700,
          color: isApproved ? "#50fa7b" : "#ff5555",
          textTransform: "uppercase",
        }}>
          {line.quarantineOutcome}
        </div>
      )}
    </div>
  );
}

/* ── Turn grouping helper ── */
interface PolicyTurn {
  turnNumber: number;
  toolLines: TerminalLine[];     // type === "tool"
  resultMap: Map<string, TerminalLine>; // toolId -> result line
  quarantineLines: TerminalLine[];
}

function groupByTurns(lines: TerminalLine[]): PolicyTurn[] {
  const turns: PolicyTurn[] = [];
  let current: PolicyTurn | null = null;
  let turnNum = 0;

  // Build a map of toolId -> result line for quick lookup
  const resultById = new Map<string, TerminalLine>();
  for (const line of lines) {
    if (line.type === "tool-result" && line.toolId) {
      resultById.set(line.toolId, line);
    }
  }

  // A new turn starts after a gap with no tool calls (i.e., after a prompt/output/system line)
  let lastWasTool = false;
  for (const line of lines) {
    if (line.type === "tool" || line.type === "quarantine") {
      if (!lastWasTool || !current) {
        turnNum++;
        current = { turnNumber: turnNum, toolLines: [], resultMap: new Map(), quarantineLines: [] };
        turns.push(current);
      }
      if (line.type === "tool") {
        current.toolLines.push(line);
        if (line.toolId && resultById.has(line.toolId)) {
          current.resultMap.set(line.toolId, resultById.get(line.toolId)!);
        }
      } else {
        current.quarantineLines.push(line);
      }
      lastWasTool = true;
    } else if (line.type === "tool-result") {
      // Skip — already associated via resultMap
      lastWasTool = true;
    } else {
      lastWasTool = false;
    }
  }
  return turns;
}

/* ── Policy Feed stats ── */
function computeStats(turns: PolicyTurn[]) {
  let total = 0, allowed = 0, blocked = 0;
  for (const turn of turns) {
    for (const tl of turn.toolLines) {
      total++;
      if (tl.policyDecision === "block") blocked++;
      else allowed++;
    }
  }
  return { total, allowed, blocked };
}

export function ChatWorkbench({ projectName }: ChatWorkbenchProps) {
  const { user } = useAuth();
  const autopilot = useAutopilotStatus();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [phase, setPhase] = useState("idle");
  const [streaming, setStreaming] = useState("");
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [activeProvider, setActiveProvider] = useState("ollama");
  const [ollamaModels, setOllamaModels] = useState<OllamaModel[]>([]);
  const [activeModel, setActiveModel] = useState("");
  const [termCollapsed, setTermCollapsed] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const userInitial = (user?.login?.charAt(0) || "U").toUpperCase();

  const navigateToSettings = useCallback(() => {
    window.location.hash = "settings";
  }, []);

  // Terminal state
  const [termLines, setTermLines] = useState<TerminalLine[]>([
    { type: "system", text: `cd ${projectName}`, timestamp: Date.now() },
    { type: "output", text: "Ready. Agent commands and tool output will appear here.", timestamp: Date.now() },
  ]);
  const [termInput, setTermInput] = useState("");
  const [termRunning, setTermRunning] = useState(false);
  const termRef = useRef<HTMLDivElement>(null);

  // Fetch providers + models on mount
  useEffect(() => {
    apiFetch("/api/providers").then(r => r.json()).then(data => {
      setProviders(data.providers || []);
      setActiveProvider(data.active || "ollama");
    }).catch(() => {});
    apiFetch("/api/ollama/status").then(r => r.json()).then(data => {
      setOllamaModels(data.models || []);
      setActiveModel(data.active_model || "");
    }).catch(() => {});
  }, []);

  const handleSelectProvider = (id: string) => {
    setActiveProvider(id);
    apiFetch("/api/provider", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider: id }),
    }).catch(() => {});
    if (id === "ollama") {
      apiFetch("/api/ollama/status").then(r => r.json()).then(data => {
        setOllamaModels(data.models || []);
        setActiveModel(data.active_model || "");
      }).catch(() => {});
    }
  };

  const handleSelectModel = (name: string) => {
    setActiveModel(name);
  };

  // Auto-scroll on new messages
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  // Focus textarea on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = 22;
    const maxHeight = lineHeight * 5;
    el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
  }, []);

  useEffect(() => {
    autoResize();
  }, [input, autoResize]);

  // Listen for SSE agent events
  useEffect(() => {
    const onText = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const text = typeof detail === "string" ? detail : detail?.text || detail?.data || String(detail || "");
      setStreaming((prev) => prev + text);
      setPhase("streaming");
    };

    const onDone = () => {
      setStreaming((prev) => {
        if (prev.trim()) {
          setMessages((msgs) => [...msgs, { role: "agent", content: prev.trim(), timestamp: nowTimestamp() }]);
        }
        return "";
      });
      setPhase("idle");
    };

    const onCancelled = () => {
      setStreaming((prev) => {
        if (prev.trim()) {
          setMessages((msgs) => [...msgs, { role: "agent", content: prev.trim(), timestamp: nowTimestamp() }]);
        }
        return "";
      });
      setPhase("idle");
    };

    const onError = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const error = typeof detail === "string" ? detail : detail?.error || detail?.message || "An error occurred";
      const code = typeof detail === "object" ? detail?.code : undefined;
      setMessages((msgs) => [...msgs, {
        role: "agent",
        content: error,
        timestamp: nowTimestamp(),
        errorCode: code || (error.toLowerCase().includes("no ai provider") || error.toLowerCase().includes("not available") || error.toLowerCase().includes("could not reach") ? "provider_unavailable" : "provider_error"),
      }]);
      setStreaming("");
      setPhase("idle");
    };

    const onPhase = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setPhase(detail?.phase || detail || "idle");
    };

    window.addEventListener("opentool-agent-text", onText);
    window.addEventListener("opentool-agent-done", onDone);
    window.addEventListener("opentool-agent-error", onError);
    window.addEventListener("opentool-agent-phase", onPhase);
    window.addEventListener("opentool-agent-cancelled", onCancelled);

    return () => {
      window.removeEventListener("opentool-agent-text", onText);
      window.removeEventListener("opentool-agent-done", onDone);
      window.removeEventListener("opentool-agent-error", onError);
      window.removeEventListener("opentool-agent-phase", onPhase);
      window.removeEventListener("opentool-agent-cancelled", onCancelled);
    };
  }, []);

  // Listen for agent tool use events
  useEffect(() => {
    const onTool = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const name = detail?.name || "unknown";
      const id = detail?.id || "";
      const inputObj = typeof detail?.input === "object" ? detail.input : {};
      const inputStr = typeof detail?.input === "string" ? detail.input : JSON.stringify(detail?.input || {});

      // If the agent is navigating the browser, open the Browser window and navigate.
      // We stash the pending URL on window so BrowserWorkbench can pick it up on mount,
      // and also dispatch the event after a delay to cover the case where it's already mounted.
      if (name === "navigate_browser" && inputObj.url) {
        (window as any).__opentool_pending_browser_url = inputObj.url;
        window.dispatchEvent(new CustomEvent("opentool-open-app", { detail: { id: "browser" } }));
        // Delay navigate event so BrowserWorkbench has time to mount if it wasn't open
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("opentool-browser-navigate", { detail: { url: inputObj.url } }));
          // Clear pending URL after dispatch
          delete (window as any).__opentool_pending_browser_url;
        }, 300);
      }

      setTermLines((prev) => [
        ...prev,
        {
          type: "tool",
          text: `[tool] ${name}: ${inputStr}`,
          timestamp: Date.now(),
          toolId: id,
          toolName: name,
          toolInput: inputObj,
          toolStatus: "executing" as const,
        },
      ]);
    };
    const onToolResult = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const name = detail?.name || "";
      const id = detail?.id || "";
      const output = typeof detail?.output === "string" ? detail.output : JSON.stringify(detail?.output || "");
      const isError = detail?.is_error === true;
      const preview = output.length > 500 ? output.slice(0, 500) + "..." : output;
      setTermLines((prev) => {
        // Try to update the matching tool line's status
        const updated = prev.map((line) => {
          if (line.type === "tool" && line.toolId === id && line.toolStatus === "executing") {
            return { ...line, toolStatus: (isError ? "error" : "done") as "error" | "done" };
          }
          return line;
        });
        return [
          ...updated,
          {
            type: "tool-result" as const,
            text: `${name ? `[${name}] ` : ""}${preview}`,
            timestamp: Date.now(),
            toolId: id,
            toolName: name,
            toolOutput: preview,
            toolIsError: isError,
            toolStatus: (isError ? "error" : "done") as "error" | "done",
          },
        ];
      });
    };

    const onPolicy = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const toolName = detail?.tool || "";
      const decision = detail?.decision || "allow";
      const reason = detail?.reason || "";
      setTermLines((prev) => {
        // Find the most recent tool line with this name and update its policy decision
        const idx = [...prev].reverse().findIndex(
          (line) => line.type === "tool" && line.toolName === toolName && !line.policyDecision
        );
        if (idx === -1) return prev;
        const realIdx = prev.length - 1 - idx;
        const updated = [...prev];
        updated[realIdx] = { ...updated[realIdx], policyDecision: decision, policyReason: reason };
        return updated;
      });
    };

    // Quarantine approval flow
    const onQuarantinePending = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const approvalId = detail?.approval_id || "";
      const tool = detail?.tool || "";
      const reason = detail?.reason || "";
      setTermLines((prev) => [
        ...prev,
        {
          type: "quarantine" as const,
          text: `[quarantine] ${tool}: ${reason}`,
          timestamp: Date.now(),
          toolName: tool,
          policyReason: reason,
          approvalId,
          quarantineOutcome: "pending" as const,
        },
      ]);
    };

    const onQuarantineResolved = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const approvalId = detail?.approval_id || "";
      const outcome = detail?.outcome || "rejected";
      setTermLines((prev) =>
        prev.map((line) =>
          line.type === "quarantine" && line.approvalId === approvalId
            ? { ...line, quarantineOutcome: outcome as "approved" | "rejected" | "timeout" }
            : line
        )
      );
    };

    window.addEventListener("opentool-agent-tool", onTool);
    window.addEventListener("opentool-agent-tool-result", onToolResult);
    window.addEventListener("opentool-agent-policy", onPolicy);
    window.addEventListener("opentool-agent-quarantine-pending", onQuarantinePending);
    window.addEventListener("opentool-agent-quarantine-resolved", onQuarantineResolved);
    return () => {
      window.removeEventListener("opentool-agent-tool", onTool);
      window.removeEventListener("opentool-agent-tool-result", onToolResult);
      window.removeEventListener("opentool-agent-policy", onPolicy);
      window.removeEventListener("opentool-agent-quarantine-pending", onQuarantinePending);
      window.removeEventListener("opentool-agent-quarantine-resolved", onQuarantineResolved);
    };
  }, []);

  // Auto-scroll terminal
  useEffect(() => {
    if (termRef.current) {
      termRef.current.scrollTop = termRef.current.scrollHeight;
    }
  }, [termLines]);

  // Execute a shell command
  const execCommand = useCallback(async (cmd: string) => {
    const trimmed = cmd.trim();
    if (!trimmed || termRunning) return;

    setTermLines((prev) => [
      ...prev,
      { type: "prompt", text: trimmed, timestamp: Date.now() },
    ]);
    setTermInput("");
    setTermRunning(true);

    try {
      const res = await apiFetch("/api/exec", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ command: trimmed }),
      });
      const data = await res.json();
      if (data.error) {
        setTermLines((prev) => [
          ...prev,
          { type: "error", text: data.error, timestamp: Date.now() },
        ]);
      } else {
        const out = (data.stdout || "") + (data.stderr ? `\n${data.stderr}` : "");
        if (out.trim()) {
          setTermLines((prev) => [
            ...prev,
            { type: "output", text: out.trim(), timestamp: Date.now() },
          ]);
        }
      }
    } catch (err) {
      setTermLines((prev) => [
        ...prev,
        { type: "error", text: `Network error: ${err}`, timestamp: Date.now() },
      ]);
    } finally {
      setTermRunning(false);
    }
  }, [termRunning]);

  const submitMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || phase === "streaming" || phase === "pending") return;

    setMessages((prev) => [...prev, { role: "user", content: trimmed, timestamp: nowTimestamp() }]);
    setInput("");
    setPhase("pending");

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    if (!window.ipc || (window.ipc as any).__web) {
      apiFetch("/api/chat", {
        method: "POST",
        body: JSON.stringify({
          prompt: trimmed,
          provider: activeProvider,
          ...(activeModel ? { model: activeModel } : {}),
        }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            const code = data.code || "provider_error";
            setMessages(prev => [...prev, {
              role: 'agent',
              content: data.error,
              timestamp: nowTimestamp(),
              errorCode: code,
            }]);
            setPhase('idle');
          }
        })
        .catch(err => {
          setMessages(prev => [...prev, {
            role: 'agent',
            content: "Could not connect to the server. Check that the backend is running.",
            timestamp: nowTimestamp(),
            errorCode: "provider_unavailable",
          }]);
          setPhase('idle');
        });
    } else {
      postIpc({ kind: "terminal-input-agent", data: trimmed });
    }
  }, [phase, activeProvider, activeModel]);

  const handleSubmit = useCallback(() => {
    submitMessage(input);
  }, [input, submitMessage]);

  const handleCancel = useCallback(() => {
    if (phase !== "pending" && phase !== "streaming" && phase !== "tool-loop" && phase !== "executing-tools") return;
    setPhase("cancelling");
    apiFetch("/api/chat/cancel", { method: "POST" }).catch(() => {
      // If cancel request fails, reset to idle
      setPhase("idle");
    });
  }, [phase]);

  // Escape key triggers cancel
  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
      }
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [handleCancel]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isActive = phase === "pending" || phase === "streaming" || phase === "tool-loop" || phase === "executing-tools" || phase === "cancelling";
  const autopilotState = autopilot.runtime?.state ?? "offline";
  const autopilotDetail =
    autopilot.current_task?.prompt ??
    autopilot.runtime?.last_event ??
    "no active runtime";
  const showEmpty = messages.length === 0 && !streaming;

  return (
    <div className="chat-layout">
      {/* Chat panel */}
      <div className="chat-panel">
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header__left">
            <div className="chat-header__icon" style={{ color: (PROVIDER_ICONS[activeProvider] || DEFAULT_PROVIDER_ICON).color }}>
              {(PROVIDER_ICONS[activeProvider] || DEFAULT_PROVIDER_ICON).icon}
            </div>
            <span className="chat-header__title">Agent Chat</span>
            <span className="chat-header__project">{projectName}</span>
          </div>
          {phase !== "idle" && (
            <div className={`chat-phase-badge ${phase}`}>
              <span className="chat-phase-badge__dot" />
              {phase === "pending" ? "Thinking" : phase === "streaming" ? "Writing" : phase === "cancelling" ? "Stopping" : phase}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="chat-messages" ref={messagesRef}>
          {showEmpty && (
            <div className="chat-empty">
              <div className="chat-empty__icon">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3 className="chat-empty__title">Start a conversation</h3>
              <p className="chat-empty__subtitle">Ask anything about your codebase, or pick a suggestion below.</p>
              <div className="chat-empty__cards">
                {SUGGESTIONS.map((s) => (
                  <button key={s.label} className="chat-suggestion-card" onClick={() => { setInput(s.label); textareaRef.current?.focus(); }}>
                    <span className="chat-suggestion-card__icon">{s.icon}</span>
                    <span className="chat-suggestion-card__label">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => {
            // Provider unavailable — show friendly card instead of error text
            if (msg.errorCode === "provider_unavailable") {
              return (
                <div key={i} className="chat-row agent">
                  <div className="chat-row__avatar agent">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </div>
                  <div className="chat-row__body">
                    <ProviderUnavailableCard message={msg.content} onConnect={navigateToSettings} />
                  </div>
                </div>
              );
            }
            // Provider error — show styled error card
            if (msg.errorCode === "provider_error") {
              return (
                <div key={i} className="chat-row agent">
                  <div className="chat-row__avatar agent">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </div>
                  <div className="chat-row__body">
                    <div className="chat-row__meta">
                      <span className="chat-row__role">AI</span>
                      {msg.timestamp && <span className="chat-row__timestamp">{formatTimestamp(msg.timestamp)}</span>}
                    </div>
                    <ProviderErrorCard message={msg.content} />
                  </div>
                </div>
              );
            }
            // Normal message
            return (
              <div key={i} className={`chat-row ${msg.role}`}>
                <div className={`chat-row__avatar ${msg.role}`}>
                  {msg.role === "user" ? (
                    <span className="chat-row__avatar-letter">{userInitial}</span>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  )}
                </div>
                <div className="chat-row__body">
                  <div className="chat-row__meta">
                    <span className="chat-row__role">{msg.role === "user" ? "You" : "AI"}</span>
                    {msg.timestamp && <span className="chat-row__timestamp">{formatTimestamp(msg.timestamp)}</span>}
                  </div>
                  <div className={`chat-bubble ${msg.role}`}>
                    {msg.role === "agent" ? renderMarkdown(msg.content) : msg.content}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Streaming in progress */}
          {streaming && (
            <div className="chat-row agent">
              <div className="chat-row__avatar agent">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </div>
              <div className="chat-row__body">
                <div className="chat-row__meta">
                  <span className="chat-row__role">AI</span>
                </div>
                <div className="chat-bubble agent chat-bubble--streaming">
                  {renderMarkdown(streaming)}
                  <span className="chat-cursor" />
                </div>
              </div>
            </div>
          )}

          {/* Thinking indicator */}
          {phase === "pending" && !streaming && (
            <div className="chat-row agent">
              <div className="chat-row__avatar agent">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </div>
              <div className="chat-row__body">
                <div className="chat-row__meta">
                  <span className="chat-row__role">AI</span>
                </div>
                <ThinkingIndicator />
              </div>
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="chat-composer">
          <div className="chat-composer__inner">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isActive ? "Agent is working..." : "Message the agent..."}
              disabled={isActive}
              className="chat-composer__textarea"
              rows={1}
            />
            <div className="chat-composer__actions">
              <ModelPill
                providers={providers}
                activeProvider={activeProvider}
                models={ollamaModels}
                activeModel={activeModel}
                onSelectProvider={handleSelectProvider}
                onSelectModel={handleSelectModel}
              />
              {isActive ? (
                <button
                  onClick={handleCancel}
                  className="chat-composer__stop"
                  title="Stop agent (Esc)"
                >
                  {phase === "cancelling" ? (
                    <SendingSpinner />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="4" y="4" width="16" height="16" rx="2" />
                    </svg>
                  )}
                  <span className="chat-composer__stop-label">
                    {phase === "cancelling" ? "Stopping" : "Stop"}
                  </span>
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!input.trim()}
                  className="chat-composer__send"
                  title="Send message"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div className="chat-composer__footer">
            <span className="chat-composer__hint">
              <kbd>Enter</kbd> to send, <kbd>Shift+Enter</kbd> for newline{isActive ? <>, <kbd>Esc</kbd> to stop</> : null}
            </span>
          </div>
        </div>
      </div>

      {/* Policy Evaluation panel */}
      {(() => {
        const turns = groupByTurns(termLines);
        const stats = computeStats(turns);
        return (
          <div className={`chat-terminal-panel ${termCollapsed ? "collapsed" : ""}`}>
            <div className="chat-terminal-header">
              <div className="chat-terminal-header__left">
                {/* Shield icon */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" />
                </svg>
                <span>Policy</span>
                <div className={`chat-terminal-autopilot is-${autopilotState}`}>
                  <span className="chat-terminal-autopilot__label">Autopilot</span>
                  <span className="chat-terminal-autopilot__state">{autopilotState}</span>
                  <span className="chat-terminal-autopilot__detail">{autopilotDetail}</span>
                </div>
                {stats.total > 0 && (
                  <span className="policy-header-stats">
                    <span className="policy-header-stats__count">{stats.total}</span> calls
                    <span className="policy-header-stats__sep" />
                    <span className="policy-header-stats__allowed">{stats.allowed}</span> allowed
                    {stats.blocked > 0 && (
                      <>
                        <span className="policy-header-stats__sep" />
                        <span className="policy-header-stats__blocked">{stats.blocked}</span> blocked
                      </>
                    )}
                  </span>
                )}
              </div>
              <button
                className="chat-terminal-header__toggle"
                onClick={() => setTermCollapsed(!termCollapsed)}
                title={termCollapsed ? "Expand panel" : "Minimize panel"}
              >
                {termCollapsed ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/></svg>
                )}
              </button>
            </div>
            {!termCollapsed && (
              <div className="chat-terminal-body">
                <div ref={termRef} className="policy-eval-feed">
                  {turns.length === 0 && (
                    <div className="policy-eval-empty">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3">
                        <path d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" />
                      </svg>
                      <span>Policy evaluations will appear here when the agent uses tools.</span>
                    </div>
                  )}
                  {turns.map((turn) => (
                    <div key={turn.turnNumber} className="policy-eval-turn">
                      <div className="policy-eval-turn__label">
                        Turn {turn.turnNumber}
                        <span className="policy-eval-turn__count">{turn.toolLines.length} call{turn.toolLines.length !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="policy-eval-turn__cards">
                        {turn.toolLines.map((tl, j) => (
                          <PolicyEvalCard
                            key={tl.toolId || j}
                            line={tl}
                            resultLine={tl.toolId ? turn.resultMap.get(tl.toolId) : undefined}
                          />
                        ))}
                        {turn.quarantineLines.map((ql, j) => (
                          <QuarantineApprovalCard key={`q-${j}`} line={ql} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="chat-terminal-input">
                  <span className="prompt">$</span>
                  <input
                    type="text"
                    value={termInput}
                    onChange={(e) => setTermInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") execCommand(termInput); }}
                    placeholder={termRunning ? "Running..." : "Enter command..."}
                    disabled={termRunning}
                  />
                  {termRunning && <span className="terminal-cursor">&#9608;</span>}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
